import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, translations } from '@/i18n/translations';

interface LanguageContextType {
  language: Language;
  changeLanguage: (lang: Language) => void;
  t: typeof translations.en;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>('en');

  useEffect(() => {
    // Check URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang') as Language;
    const isValidLang = (lang: Language | null | string): lang is Language =>
      ['en', 'el', 'fr', 'de', 'ar'].includes(lang as Language);

    // Check localStorage
    const storedLang = localStorage.getItem('language') as Language;

    if (langParam && isValidLang(langParam)) {
      setLanguage(langParam);
      localStorage.setItem('language', langParam);
    } else if (storedLang && isValidLang(storedLang)) {
      setLanguage(storedLang);
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('lang', storedLang);
      window.history.replaceState({}, '', newUrl.toString());
    } else {
      // Default to English
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('lang', 'en');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, []);

  useEffect(() => {
    // Keep the HTML document's lang/dir in sync with the active language
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('lang', lang);
    window.history.replaceState({}, '', newUrl.toString());
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    // Safe fallback to prevent crashes during HMR or isolated renders
    return {
      language: 'en' as Language,
      changeLanguage: () => {},
      t: translations.en,
    };
  }
  return context;
};
